/**
 * Paths the agent reports for a tool (locations, arguments, diff blocks) — safe under the session parent path.
 */

import { resolveWorkspaceFilePath } from '../workspace-files.js';

function addPath(sessionParentPath: string, raw: unknown, out: Set<string>): void {
  if (typeof raw !== 'string') return;
  const trimmed = raw.trim();
  if (!trimmed) return;
  const resolved = resolveWorkspaceFilePath(sessionParentPath, trimmed);
  if (!resolved) return;
  out.add(resolved.display);
}

function walkLocations(sessionParentPath: string, loc: unknown, out: Set<string>): void {
  if (!Array.isArray(loc)) return;
  for (const item of loc) {
    if (item && typeof item === 'object') addPath(sessionParentPath, (item as Record<string, unknown>).path, out);
  }
}

const PATH_KEYS = [
  'path',
  'file',
  'filePath',
  'filepath',
  'target_path',
  'targetPath',
  'file_path',
  'target_file',
] as const;

function collectFromObject(sessionParentPath: string, obj: Record<string, unknown>, out: Set<string>, depth: number): void {
  if (depth > 10) return;
  for (const k of PATH_KEYS) {
    if (k in obj) addPath(sessionParentPath, obj[k], out);
  }
  for (const v of Object.values(obj)) {
    if (v != null && typeof v === 'object') collectUnknown(sessionParentPath, v, out, depth + 1);
  }
}

function collectUnknown(sessionParentPath: string, v: unknown, out: Set<string>, depth: number): void {
  if (depth > 10 || v == null) return;
  if (Array.isArray(v)) {
    for (const x of v) collectUnknown(sessionParentPath, x, out, depth + 1);
    return;
  }
  if (typeof v === 'object') collectFromObject(sessionParentPath, v as Record<string, unknown>, out, depth);
}

function walkContentArray(sessionParentPath: string, content: unknown, out: Set<string>): void {
  if (!Array.isArray(content)) return;
  for (const item of content) {
    if (!item || typeof item !== 'object') continue;
    const o = item as Record<string, unknown>;
    if (typeof o.type === 'string' && o.type.toLowerCase() === 'diff') {
      for (const k of PATH_KEYS) if (k in o) addPath(sessionParentPath, o[k], out);
    }
    if (o.type === 'content' && o.content != null && typeof o.content === 'object') {
      const inner = o.content as Record<string, unknown>;
      if (typeof inner.type === 'string' && inner.type.toLowerCase() === 'diff') {
        for (const k of PATH_KEYS) if (k in inner) addPath(sessionParentPath, inner[k], out);
      }
    }
  }
}

/**
 * Display paths (posix-ish, relative to session parent) the tool targets, from an ACP session/update payload.
 */
export function extractToolTargetDisplayPaths(update: unknown, sessionParentPath: string): string[] {
  const out = new Set<string>();
  if (!update || typeof update !== 'object') return [];
  const u = update as Record<string, unknown>;

  walkLocations(sessionParentPath, u.locations, out);
  walkLocations(sessionParentPath, u.fileLocations, out);
  walkLocations(sessionParentPath, u.file_locations, out);

  const tc = (u.toolCall ?? u.tool_call) as Record<string, unknown> | undefined;
  if (tc && typeof tc.arguments === 'string') {
    try {
      const parsed = JSON.parse(tc.arguments) as unknown;
      collectUnknown(sessionParentPath, parsed, out, 0);
    } catch {
      /* ignore */
    }
  }

  walkContentArray(sessionParentPath, u.content, out);
  collectFromObject(sessionParentPath, u, out, 0);

  return [...out];
}
